import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import Textarea from "@/components/ui/textarea";
import DatePicker from "@/components/ui/datepicker";
import FileInput from "@/components/ui/file-input";
import {
  Edit as EditIcon,
  Plus as PlusIcon,
  Pdf as PdfIcon,
  Delete as DeleteIcon,
} from "@/components/icons";
import type {
  BlackListEntry,
  BlackListFormValues,
  BlackListReason,
} from "../types/black-list.types";
import { parseDate, formatDate } from "../utils/dateUtils";
import { getAdminUsers } from "@/lib/users.api";
import styles from "./BlackListFormDrawer.module.scss";

const buildReasonOptions = (
  t: (k: string) => string,
): { label: string; value: BlackListReason }[] => [
  { label: t("blackList.absence"), value: "Devamsızlık" },
  { label: t("blackList.failure"), value: "Başarısızlık" },
];

const PERSONNEL_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const emptyForm: BlackListFormValues = {
  personnelId: "",
  personnelName: "",
  startDate: "",
  endDate: "",
  reason: "Devamsızlık",
  description: "",
  fileIds: [],
  supportingDocuments: [],
};

interface BlackListFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  entry: BlackListEntry | null;
  onSubmit: (data: BlackListFormValues, id?: string) => void;
}

const BlackListFormDrawer: React.FC<BlackListFormDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  entry,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const REASON_OPTIONS = buildReasonOptions(t);
  const [form, setForm] = useState<BlackListFormValues>(emptyForm);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  /** Düzenlemede mevcut dosyalardan silmek istediklerimiz */
  const [removedExistingFileIds, setRemovedExistingFileIds] = useState<
    string[]
  >([]);
  const [personOptions, setPersonOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [isLoadingPersonnel, setIsLoadingPersonnel] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<"personnelId" | "startDate" | "endDate" | "reason", string>>
  >({});
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mode === "edit" && entry) {
      setForm({
        personnelId: entry.personnelId,
        personnelName: entry.personnelName ?? "",
        startDate: entry.startDate,
        endDate: entry.endDate,
        reason: entry.reason,
        description: entry.description ?? "",
      });
      setUploadedFileIds([]);
      setRemovedExistingFileIds([]);
      setPersonOptions(
        entry.personnelId && entry.personnelName
          ? [{ label: entry.personnelName, value: entry.personnelId }]
          : [],
      );
    } else {
      setForm(emptyForm);
      setUploadedFileIds([]);
      setRemovedExistingFileIds([]);
      setPersonOptions([]);
      setFieldErrors({});
    }
  }, [mode, entry, isOpen]);

  const fetchPersonnel = useCallback(
    async (search: string, currentId?: string, currentLabel?: string) => {
      setIsLoadingPersonnel(true);
      try {
        const { data } = await getAdminUsers({
          limit: PERSONNEL_PAGE_SIZE,
          page: 1,
          search: search.trim() || undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        const options = data.map((u) => {
          const label =
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.email ||
            u.id;
          return { label, value: u.id };
        });
        if (
          currentId &&
          currentLabel &&
          !options.some((o) => o.value === currentId)
        ) {
          setPersonOptions([
            { label: currentLabel, value: currentId },
            ...options,
          ]);
        } else {
          setPersonOptions(options);
        }
      } catch {
        setPersonOptions(
          currentId && currentLabel
            ? [{ label: currentLabel, value: currentId }]
            : [],
        );
      } finally {
        setIsLoadingPersonnel(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "add") {
      fetchPersonnel("", undefined, undefined);
    }
  }, [isOpen, mode, fetchPersonnel]);

  const handlePersonnelSearch = (value: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchPersonnel(
        value,
        form.personnelId || undefined,
        form.personnelName || undefined,
      );
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleChange = (field: keyof BlackListFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePersonnelSelect = (value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    const opt = personOptions.find((o) => o.value === v);
    setForm((prev) => ({
      ...prev,
      personnelId: v ?? prev.personnelId,
      personnelName: opt?.label ?? prev.personnelName,
    }));
    if (fieldErrors.personnelId) {
      setFieldErrors((prev) => ({ ...prev, personnelId: "" }));
    }
  };

  const handleStartDateChange = (
    value: Date | [Date | null, Date | null] | null,
  ) => {
    const date = Array.isArray(value) ? (value[0] ?? null) : value;
    setForm((prev) => ({ ...prev, startDate: formatDate(date) }));
    if (fieldErrors.startDate) {
      setFieldErrors((prev) => ({ ...prev, startDate: "" }));
    }
  };

  const handleEndDateChange = (
    value: Date | [Date | null, Date | null] | null,
  ) => {
    const date = Array.isArray(value) ? (value[1] ?? value[0] ?? null) : value;
    setForm((prev) => ({ ...prev, endDate: formatDate(date) }));
    if (fieldErrors.endDate) {
      setFieldErrors((prev) => ({ ...prev, endDate: "" }));
    }
  };

  const handleReasonSelect = (value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    if (v === "Devamsızlık" || v === "Başarısızlık") {
      setForm((prev) => ({ ...prev, reason: v }));
      if (fieldErrors.reason) {
        setFieldErrors((prev) => ({ ...prev, reason: "" }));
      }
    }
  };

  const handleUploadSuccess = (fileId: string) => {
    setUploadedFileIds((prev) => [...prev, fileId]);
  };

  const handleRemoveExistingFile = (fileId: string) => {
    setRemovedExistingFileIds((prev) => [...prev, fileId]);
  };

  /** Düzenlemede: kalan mevcut id'ler + yeni yüklenen id'ler */
  const keptExistingIds =
    mode === "edit" && entry?.files
      ? entry.files
          .filter((f) => !removedExistingFileIds.includes(f.id))
          .map((f) => f.id)
      : [];
  const allFileIds = [...keptExistingIds, ...uploadedFileIds];

  const handleSubmit = () => {
    const errors: Partial<
      Record<"personnelId" | "startDate" | "endDate" | "reason", string>
    > = {};

    if (!form.personnelId) {
      errors.personnelId = t("blackList.form.validation.personnelRequired");
    }
    if (!form.startDate) {
      errors.startDate = t("blackList.form.validation.startDateRequired");
    }
    if (!form.endDate) {
      errors.endDate = t("blackList.form.validation.endDateRequired");
    }
    if (!form.reason) {
      errors.reason = t("blackList.form.validation.reasonRequired");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    onSubmit(
      { ...form, fileIds: allFileIds.length ? allFileIds : undefined },
      entry?.id,
    );
    onClose();
  };

  const headerTitle = (
    <span className={styles.headerTitle}>
      {mode === "edit" ? (
        <>
          <EditIcon width={24} height={24} color="#14181F" />
          {t("blackList.form.editTitle")}
        </>
      ) : (
        <>
          <PlusIcon width={24} height={24} color="#14181F" />
          {t("blackList.form.addTitle")}
        </>
      )}
    </span>
  );

  const footer = (
    <div className={styles.footer}>
      <Button variant="outlined" color="default" onClick={onClose}>
        {t("buttons.cancel")}
      </Button>
      <Button variant="contained" color="primary" onClick={handleSubmit}>
        {mode === "edit"
          ? t("buttons.save")
          : t("blackList.form.addButton")}
      </Button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      width="561px"
      position="right"
      footer={footer}
    >
      <div className={styles.drawerContent}>
        {/* Row 1: Personel Ad Soyad */}
        <Dropdown
          label={t("blackList.form.personnelName") + "*"}
          placeholder={
            isLoadingPersonnel
              ? t("blackList.form.personnelSearching")
              : t("blackList.form.personnelPlaceholder")
          }
          options={personOptions}
          value={form.personnelId || null}
          selectedLabel={form.personnelName || undefined}
          onSelect={handlePersonnelSelect}
          onSearch={handlePersonnelSearch}
          searchable
          error={!!fieldErrors.personnelId}
          errorMessage={fieldErrors.personnelId}
        />
        {/* Row 2: Başlangıç Tarihi | Bitiş Tarihi */}
        <div className={styles.twoColumnRow}>
          <DatePicker
            label={t("blackList.form.startDate") + "*"}
            placeholder={t("blackList.form.datePlaceholder")}
            value={parseDate(form.startDate)}
            onChange={handleStartDateChange}
            format="dd.MM.yyyy"
            locale="tr"
            variant="outlined"
            error={!!fieldErrors.startDate}
            errorMessage={fieldErrors.startDate}
          />
          <DatePicker
            label={t("blackList.form.endDate") + "*"}
            placeholder={t("blackList.form.datePlaceholder")}
            value={parseDate(form.endDate)}
            onChange={handleEndDateChange}
            format="dd.MM.yyyy"
            locale="tr"
            variant="outlined"
            error={!!fieldErrors.endDate}
            errorMessage={fieldErrors.endDate}
          />
        </div>
        {/* Row 3: Nedeni (full width) */}
        <Dropdown
          label={t("blackList.form.reason") + "*"}
          placeholder={t("blackList.form.reasonPlaceholder")}
          options={REASON_OPTIONS}
          value={form.reason}
          selectedLabel={form.reason}
          onSelect={handleReasonSelect}
          error={!!fieldErrors.reason}
          errorMessage={fieldErrors.reason}
        />
        {/* Row 4: Açıklama (full width) */}
        <Textarea
          label={t("blackList.form.description")}
          placeholder={t("blackList.form.descriptionPlaceholder")}
          value={form.description}
          maxLength={2000}
          onChange={(e) => handleChange("description", e.target.value)}
          variant="outlined"
          rows={4}
        />
        {/* Row 5: Destekleyici Belgeler - düzenlemede mevcut dosyalar + yeni yükleme */}
        <div className={styles.fieldGroup}>
          <label>
            {t("blackList.form.supportingDocuments")}
          </label>
          {mode === "edit" && entry?.files && entry.files.length > 0 && (
            <div className={styles.existingFilesList}>
              {entry.files
                .filter((f) => !removedExistingFileIds.includes(f.id))
                .map((file) => (
                  <div key={file.id} className={styles.documentItem}>
                    <PdfIcon
                      width={24}
                      height={24}
                      className={styles.documentIcon}
                    />
                    <div className={styles.documentInfoWrap}>
                      <span className={styles.documentInfo}>
                        {file.originalName}
                      </span>
                      {file.size != null && (
                        <span className={styles.documentSize}>
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                      )}
                    </div>
                    <a
                      href={file.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.downloadLink}
                      title={t("common.download")}
                    >
                      {t("common.download")}
                    </a>
                    <button
                      type="button"
                      className={styles.removeFileBtn}
                      onClick={() => handleRemoveExistingFile(file.id)}
                      title={t("common.delete")}
                      aria-label={t("common.delete")}
                    >
                      <DeleteIcon width={18} height={18} />
                    </button>
                  </div>
                ))}
            </div>
          )}
          <FileInput
            placeholder={t("blackList.form.filePlaceholder")}
            uploadToBackend
            onUploadSuccess={handleUploadSuccess}
            supportedFormats={["PDF", "DOC", "DOCX"]}
            maxSize={500}
            variant="outlined"
            fileType="file"
            multiple
            maxFiles={10}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default BlackListFormDrawer;
